import { useEffect, useMemo, useState } from 'react';
import { Tag, Plus, Search, Edit3, Trash2, Calendar, CheckSquare, Square, RefreshCw } from 'lucide-react';
import { Card, SectionHeader, Badge, Button, Empty } from '@/components/ui';
import { BUSINESS_CATEGORIES } from '@/lib/data_registry';
import { categoriesApi } from '@/lib/api';
import { slugify } from '@/lib/format';

interface CategoryItem {
  id: string;
  name: string;
  count: number;
  created_at: string;
  status: 'active' | 'archived';
}

export default function CategoriesManager() {
  const [categories, setCategories] = useState<CategoryItem[]>(() => {
    const saved = localStorage.getItem('localhub_custom_categories');
    const customList: CategoryItem[] = saved ? JSON.parse(saved).map((c: any, idx: number) => ({
      id: `custom-cat-${idx + 1}`,
      name: c.name,
      count: c.count ?? 0,
      created_at: c.created_at || new Date().toISOString().split('T')[0],
      status: 'active' as const
    })) : [];

    const baseList: CategoryItem[] = BUSINESS_CATEGORIES.map((cat, idx) => ({
      id: `cat-${idx + 1}`,
      name: cat,
      count: 0,
      created_at: new Date(Date.now() - (idx * 3600 * 24 * 1000)).toISOString().split('T')[0],
      status: 'active' as const
    }));

    const existingNames = new Set(baseList.map(b => b.name.toLowerCase()));
    const uniqueCustom = customList.filter(c => !existingNames.has(c.name.toLowerCase()));
    return [...uniqueCustom, ...baseList];
  });

  // Query actual live listing counts and custom categories from FastAPI backend
  useEffect(() => {
    void (async () => {
      try {
        const [dbCats, catStats] = await Promise.all([
          categoriesApi.getCategories().catch(() => []),
          categoriesApi.getCategoryStats().catch(() => ({} as Record<string, number>))
        ]);

        const countsMap: Record<string, number> = {};
        if (catStats) {
          Object.entries(catStats).forEach(([catName, count]) => {
            if (catName) {
              countsMap[catName.trim().toLowerCase()] = count;
            }
          });
        }

        setCategories(prev => {
          const baseList = BUSINESS_CATEGORIES.map((cat, idx) => ({
            id: `cat-${idx + 1}`,
            name: cat,
            count: countsMap[cat.trim().toLowerCase()] ?? 0,
            created_at: new Date(Date.now() - (idx * 3600 * 24 * 1000)).toISOString().split('T')[0],
            status: 'active' as const
          }));

          const dbCustomList: CategoryItem[] = dbCats ? dbCats.map((c: any, idx: number) => ({
            id: `db-cat-${c.id || idx + 1}`,
            name: c.name,
            count: countsMap[c.name.trim().toLowerCase()] ?? 0,
            created_at: c.created_at || new Date().toISOString().split('T')[0],
            status: 'active' as const
          })) : [];

          const existingNames = new Set(baseList.map(b => b.name.toLowerCase()));
          const uniqueDbCustom = dbCustomList.filter(c => !existingNames.has(c.name.toLowerCase()));

          // Merge fallbacks from localStorage if any
          const saved = localStorage.getItem('localhub_custom_categories');
          const localCustomList = saved ? JSON.parse(saved) : [];
          const mergedCustom = [...uniqueDbCustom];
          localCustomList.forEach((c: any) => {
            if (!existingNames.has(c.name.toLowerCase()) && !mergedCustom.some(m => m.name.toLowerCase() === c.name.toLowerCase())) {
              mergedCustom.push({
                id: `local-cat-${mergedCustom.length + 1}`,
                name: c.name,
                count: countsMap[c.name.trim().toLowerCase()] ?? 0,
                created_at: c.created_at || new Date().toISOString().split('T')[0],
                status: 'active' as const
              });
            }
          });

          return [...mergedCustom, ...baseList];
        });
      } catch (e) {
        console.error('Failed to load dynamic categories:', e);
      }
    })();
  }, []);

  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | '7days' | '30days'>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [editingItem, setEditingItem] = useState<CategoryItem | null>(null);

  // Filtered List
  const filtered = useMemo(() => {
    return categories.filter((c) => {
      const matchSearch = c.name.toLowerCase().includes(search.toLowerCase());
      if (!matchSearch) return false;

      if (dateFilter === '7days') {
        const d = new Date(c.created_at).getTime();
        const past = Date.now() - 7 * 24 * 3600 * 1000;
        return d >= past;
      }
      if (dateFilter === '30days') {
        const d = new Date(c.created_at).getTime();
        const past = Date.now() - 30 * 24 * 3600 * 1000;
        return d >= past;
      }
      return true;
    });
  }, [categories, search, dateFilter]);

  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map(c => c.id));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCatName.trim()) return;
    const catName = newCatName.trim();
    const catSlug = slugify(catName);

    try {
      await categoriesApi.createCategory({ name: catName, slug: catSlug });
    } catch (err) {
      console.error('Failed to save category to backend:', err);
    }

    const newItem: CategoryItem = {
      id: `cat-${Date.now()}`,
      name: catName,
      count: 0,
      created_at: new Date().toISOString().split('T')[0],
      status: 'active'
    };
    setCategories(prev => {
      const updated = [newItem, ...prev];
      const customOnly = updated.filter(item => !BUSINESS_CATEGORIES.includes(item.name));
      localStorage.setItem('localhub_custom_categories', JSON.stringify(customOnly));
      return updated;
    });
    setNewCatName('');
    setShowAddModal(false);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem || !editingItem.name.trim()) return;
    const catName = editingItem.name.trim();
    const catSlug = slugify(catName);

    try {
      await categoriesApi.createCategory({ name: catName, slug: catSlug });
    } catch (err) {
      console.error('Failed to save category to backend:', err);
    }

    setCategories(prev => {
      const updated = prev.map(c => c.id === editingItem.id ? editingItem : c);
      const customOnly = updated.filter(item => !BUSINESS_CATEGORIES.includes(item.name));
      localStorage.setItem('localhub_custom_categories', JSON.stringify(customOnly));
      return updated;
    });
    setEditingItem(null);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const itemsToDelete = categories.filter(c => selectedIds.includes(c.id));
    for (const item of itemsToDelete) {
      const catSlug = slugify(item.name);
      try {
        await categoriesApi.deleteCategory(catSlug);
      } catch (err) {
        console.error('Failed to delete category from backend:', err);
      }
    }
    setCategories(prev => prev.filter(c => !selectedIds.includes(c.id)));
    setSelectedIds([]);
  };

  return (
    <div className="space-y-6 text-slate-800">
      <SectionHeader
        title="Business Categories Management"
        subtitle={`Super Admin control panel for all ${categories.length.toLocaleString()} registered categories and services.`}
        right={
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4" /> Add New Category
          </Button>
        }
      />

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-extrabold">Total Categories</div>
          <div className="text-3xl font-extrabold text-slate-900 mt-1 tabular-nums">{categories.length.toLocaleString()}</div>
          <div className="text-xs font-bold text-emerald-600 mt-1">Imported from services_csv.csv</div>
        </Card>
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-extrabold">Selected Items</div>
          <div className="text-3xl font-extrabold text-slate-900 mt-1 tabular-nums">{selectedIds.length}</div>
          <div className="text-xs font-bold text-slate-500 mt-1">Multi-select active</div>
        </Card>
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-extrabold">Filter Results</div>
          <div className="text-3xl font-extrabold text-slate-900 mt-1 tabular-nums">{filtered.length.toLocaleString()}</div>
          <div className="text-xs font-bold text-emerald-600 mt-1">Matching search criteria</div>
        </Card>
      </div>

      {/* Control Toolbar */}
      <Card className="p-4 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 w-full md:w-auto flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search category name..."
              className="w-full h-10 pl-9 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 outline-none focus:border-emerald-600"
            />

          </div>
          <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl p-1 text-xs">
            <button
              onClick={() => setDateFilter('all')}
              className={`px-3 py-1 rounded-lg font-bold transition cursor-pointer ${dateFilter === 'all' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600'}`}
            >
              All Time
            </button>
            <button
              onClick={() => setDateFilter('7days')}
              className={`px-3 py-1 rounded-lg font-bold transition cursor-pointer ${dateFilter === '7days' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600'}`}
            >
              Last 7 Days
            </button>
            <button
              onClick={() => setDateFilter('30days')}
              className={`px-3 py-1 rounded-lg font-bold transition cursor-pointer ${dateFilter === '30days' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600'}`}
            >
              Last 30 Days
            </button>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-600">{selectedIds.length} selected</span>
            <Button variant="danger" size="sm" onClick={handleBulkDelete}>
              <Trash2 className="h-3.5 w-3.5" /> Delete Selected
            </Button>
          </div>
        )}
      </Card>

      {/* Categories Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto max-h-[600px] scrollbar-thin">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider sticky top-0 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <button onClick={toggleSelectAll} className="text-slate-400 hover:text-slate-800">
                    {selectedIds.length === filtered.length && filtered.length > 0 ? <CheckSquare className="h-4 w-4 text-emerald-600" /> : <Square className="h-4 w-4" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left">Category Name</th>
                <th className="px-4 py-3 text-right">Tagged Listings</th>
                <th className="px-4 py-3 text-center">Created Date</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-semibold text-slate-800">
              {filtered.slice(0, 100).map((cat) => {
                const isSelected = selectedIds.includes(cat.id);
                return (
                  <tr key={cat.id} className={`hover:bg-slate-50 transition ${isSelected ? 'bg-emerald-50/40' : ''}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleSelect(cat.id)} className="text-slate-400 hover:text-slate-800">
                        {isSelected ? <CheckSquare className="h-4 w-4 text-emerald-600" /> : <Square className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-bold text-slate-900 flex items-center gap-2">
                      <Tag className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      <span>{cat.name}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-extrabold text-emerald-700">{cat.count}</td>
                    <td className="px-4 py-3 text-center text-slate-500 font-mono">{cat.created_at}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge tone="success">Active</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditingItem(cat)}
                          className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
                          title="Edit Category"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setCategories(prev => prev.filter(c => c.id !== cat.id))}
                          className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition"
                          title="Delete Category"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full border border-slate-200 shadow-xl space-y-4">
            <h3 className="text-base font-bold text-slate-900">Add New Category</h3>
            <form onSubmit={handleAddCategory} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Category Title *</label>
                <input
                  type="text"
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  placeholder="e.g. Solar Energy Suppliers"
                  className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none focus:border-emerald-600"
                  required
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="ghost" type="button" onClick={() => setShowAddModal(false)}>Cancel</Button>
                <Button type="submit">Create Category</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full border border-slate-200 shadow-xl space-y-4">
            <h3 className="text-base font-bold text-slate-900">Edit Category</h3>
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Category Title *</label>
                <input
                  type="text"
                  value={editingItem.name}
                  onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                  className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none focus:border-emerald-600"
                  required
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="ghost" type="button" onClick={() => setEditingItem(null)}>Cancel</Button>
                <Button type="submit">Save Changes</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
